import React, { useContext, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import CustomText from "../atomic/CustomText";
import UserInformationProps from "../../services/models/userInformationProps.model";
import CustomImageViewer from "../molecular/CustomImageViewer";
import { useOktaAuth } from "@okta/okta-react";
import { PopUpContext } from "../../services/context/popupContext";
import TermsAndConditionsPopup from "../popups/termsAndConditionPopup";
import { useAdminContext } from "../../services/context/TourPopupContext";
import { ApplicationState } from "../../services/context/ApplicationStateContext";
import { datadogRum } from "@datadog/browser-rum";
import { clearPersistedQueryCache } from "../../services/query/persistedQueryClient";
import { beginLogout, endLogout } from "../../services/session/logoutState";
type DropDownListProps = Readonly<
  UserInformationProps & {
    dropDownRef: any;
    closeDropDown: () => void;
    handleTermsAndConditions: () => void;
  }
>;

function UserInformation({
  imageUrl,
  fullName,
  email,
}: Readonly<UserInformationProps>): JSX.Element {
  return (
    <div className="items-center px-4 py-[12.5px] flex border-b cursor-pointer overflow-hidden">
      <div className="mt-1">
        <CustomImageViewer
          imageTitle={`${fullName}image`}
          imageUrl={imageUrl}
          className="object-fit !w-8 !h-8 !rounded-full "
        />
      </div>

      <div className="ml-2">
        <CustomText
          text={fullName}
          fontWeight="font-normal"
          fontSize="text-sm"
          className="text-royalBlue capitalize profileDropdownUserAndEmail"
        />
        <CustomText
          text={email}
          fontWeight="font-normal"
          fontSize="text-xs"
          className="!text-textSecondaryGray profileDropdownUserAndEmail"
        />
      </div>
    </div>
  );
}

function DropDownList({
  dropDownRef,
  closeDropDown,
  handleTermsAndConditions,
  ...userInfoProps
}: DropDownListProps): JSX.Element {
  const { oktaAuth } = useOktaAuth();
  const { dispatch } = useContext(ApplicationState);
  const logout = async () => {
    closeDropDown();
    beginLogout();
    datadogRum?.clearUser();
    dispatch({ type: "RESET" });
    try {
      await clearPersistedQueryCache();
      localStorage.clear();
      await oktaAuth.signOut();
      endLogout();
    } catch (error) {
      endLogout();
      console.error("Logout failed", error);
    }
  };

  return (
    <div ref={dropDownRef} className="absolute top-full right-0">
      <div className="border origin-top-right min-h-[50px] max-h-[calc(100vh-50px)] overflow-auto min-w-40 rounded-md shadow-lg bg-white">
        <UserInformation {...userInfoProps} />
        <div className="py-1">
          <Link
            to="/access/profile"
            className="profileDropdownItem w-full text-left"
            onClick={closeDropDown}
          >
            <CustomText
              text="Profile"
              fontWeight="font-normal"
              fontSize="text-sm"
            />
          </Link>

          <button
            className="profileDropdownItem w-full text-left"
            onClick={handleTermsAndConditions}
          >
            <CustomText
              text="Terms of Use"
              fontWeight="font-normal"
              fontSize="text-sm"
            />
          </button>
        </div>

        <button
          onClick={logout}
          className="profileDropdownItem py-2 block border-t px-4  hover:bg-gray-100 hover:text-gray-900 w-full"
        >
          <CustomText
            text="Logout"
            fontWeight="font-normal"
            fontSize="text-sm"
            className="text-left"
          />
        </button>
      </div>
    </div>
  );
}

const ProfileDropdown = ({
  data,
  isLoading,
  isError,
}: {
  data: any;
  isLoading: boolean;
  isError: boolean;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropDownRef = useRef<any>();
  const { isTourActive } = useAdminContext();
  const oktaAuth = useOktaAuth();
  useEffect(() => {
    const handleMouseDown = (event: MouseEvent) => {
      if (dropDownRef?.current && !dropDownRef?.current?.contains(event.target)) {
        setIsOpen(false);
      }
    };

    const runtimeWindow =
      typeof globalThis !== "undefined" && "window" in globalThis
        ? (globalThis as typeof globalThis & { window: Window }).window
        : undefined;

    runtimeWindow?.addEventListener("mousedown", handleMouseDown);
    if (runtimeWindow) {
      return () => runtimeWindow.removeEventListener("mousedown", handleMouseDown);
    }
    return () => undefined;
  }, []);

  const {
    visible,
    setVisible,
    setPopUp,
    setPopUpTitle,
    setHideClose,
  } = useContext(PopUpContext);

  function openTermsAndConditions(hideClose?: boolean) {
    setVisible(!visible);
    setPopUpTitle("Terms of Use");
    setHideClose(!!hideClose);
    setPopUp(
      <TermsAndConditionsPopup
        profileData={data}
        termsAccepted={data?.user?.acceptTerms}
        oktaAuth={oktaAuth}
        id=""
      />
    );
    setIsOpen(false);
  }

  return (
    <div className="relative ml-2 lg:ml-5 z-[999]">
      <div className="flex justify-center items-center">
        <button
          type="button"
          onClick={() => (isTourActive ? undefined : setIsOpen(!isOpen))}
          className={`${isTourActive && "cursor-not-allowed"
            } rounded-full w-8 h-8`}
        >
          <CustomImageViewer
            className={`object-fit w-full h-full !rounded-full ${!isOpen && "hover:w-[30px] hover:h-[30px]"
              }`}
            imageTitle="User image"
            showLoadingIndication={isLoading}
            imageUrl={data?.user?.imageUrl || data?.user?.profileImage || ""}
          />
        </button>
      </div>
      {isOpen && (
        <DropDownList
          dropDownRef={dropDownRef}
          closeDropDown={() => setIsOpen(false)}
          fullName={`${data?.user?.firstName} ${data?.user?.lastName}`}
          email={data?.user?.email}
          imageUrl={data?.user?.imageUrl || data?.user?.profileImage || ""}
          handleTermsAndConditions={() => openTermsAndConditions(false)}
        />
      )}
    </div>
  );
};

export default ProfileDropdown;
